/**
 * Contract Setup Service
 * Manages the contract and payment setup workflow for dancers
 * Follows Service Layer Architecture with Result pattern and structured logging
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { Result } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import type { Database } from '@kit/supabase/database';

// Contract setup workflow states
export type ContractSetupStatus =
  | 'not_started' // User hasn't begun contract setup
  | 'profile_incomplete' // Profile not 100% complete
  | 'payment_setup' // Profile complete, payment information needed
  | 'identity_rejected' // Identity verification was rejected
  | 'contract_review' // Payment added, contract ready for review
  | 'contract_signed' // Contract signed, fully ready for work
  | 'contract_expired'; // Contract needs renewal

export interface ContractSetupState {
  status: ContractSetupStatus;
  profileCompletion: number;
  hasPaymentMethod: boolean;
  contractSignedAt: Date | null;
  nextStepTitle: string;
  nextStepDescription: string;
  actionUrl: string;
  actionLabel: string;
  canProceed: boolean;
}

export interface PaymentMethodInfo {
  type: 'bank_account' | 'paypal' | 'crypto';
  bankName?: string;
  accountLast4?: string;
  paypalEmail?: string;
  cryptoAddress?: string;
  isVerified: boolean;
  createdAt: Date;
}

/**
 * Service class for handling contract setup workflow
 * Provides type-safe operations with proper error handling
 */
export class ContractSetupService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Get current contract setup state for a user
   * Analyzes profile completion, identity verification, and payment status
   * Uses execute() for consistent error handling
   */
  async getContractSetupState(
    userId: string,
  ): Promise<Result<ContractSetupState>> {
    return this.execute(async () => {
      const context = { userId, operation: 'getContractSetupState' };
      this.log('info', 'Getting contract setup state for user', context);

      // Get profile completion percentage
      const profileResult = await this.getProfileCompletionPercentage(userId);
      if (profileResult.isFailure) {
        this.log('warn', 'Profile completion failed, using 0%', {
          ...context,
          error: profileResult.getError().message,
        });
      }
      const profileCompletion = profileResult.isSuccess
        ? profileResult.getValue()
        : 0;

      // Check payment method status
      const paymentResult = await this.hasPaymentMethod(userId);
      if (paymentResult.isFailure) {
        this.log('warn', 'Payment method check failed, using false', {
          ...context,
          error: paymentResult.getError().message,
        });
      }
      const hasPaymentMethod = paymentResult.isSuccess
        ? paymentResult.getValue()
        : false;

      // Get contract signing status
      const contractResult = await this.getContractStatus(userId);
      if (contractResult.isFailure) {
        this.log('warn', 'Contract status check failed, using null', {
          ...context,
          error: contractResult.getError().message,
        });
      }
      const contractSignedAt = contractResult.isSuccess
        ? contractResult.getValue()
        : null;

      // Determine workflow status and next steps
      const status = this.determineContractStatus(
        profileCompletion,
        hasPaymentMethod,
        contractSignedAt,
      );

      const state = this.buildContractSetupState(
        status,
        profileCompletion,
        hasPaymentMethod,
        contractSignedAt,
      );

      this.log('info', 'Contract setup state determined', {
        ...context,
        status,
        profileCompletion,
        hasPaymentMethod,
        contractSigned: !!contractSignedAt,
      });

      return state;
    }, 'Failed to get contract setup state');
  }

  /**
   * Initialize contract setup for a user
   * Creates contract_setups record if not exists
   * Uses execute() for consistent error handling
   */
  async initializeContractSetup(
    userId: string,
  ): Promise<Result<{ success: boolean }>> {
    return this.execute(async () => {
      const context = { userId, operation: 'initializeContractSetup' };
      this.log('info', 'Initializing contract setup for user', context);

      // Check if contract setup already exists
      const { data: existingSetup } = await this.client
        .from('contract_setups')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (!existingSetup) {
        // Create new contract setup record
        const { error: insertError } = await this.client
          .from('contract_setups')
          .insert({
            user_id: userId,
            status: 'not_started',
            started_at: new Date().toISOString(),
          });

        if (insertError) {
          this.log('error', 'Failed to initialize contract setup', {
            ...context,
            error: insertError,
          });
          throw this.handleDatabaseError(insertError);
        }

        this.log('info', 'Contract setup initialized', context);
      } else {
        this.log('info', 'Contract setup already exists', context);
      }

      return { success: true };
    }, 'Contract setup initialization failed');
  }

  /**
   * Update contract setup status
   * Uses execute() for consistent error handling
   */
  async updateContractSetupStatus(
    userId: string,
    status: ContractSetupStatus,
  ): Promise<Result<{ success: boolean }>> {
    return this.execute(async () => {
      const context = {
        userId,
        status,
        operation: 'updateContractSetupStatus',
      };
      this.log('info', 'Updating contract setup status', context);

      const { error } = await this.client.from('contract_setups').upsert({
        user_id: userId,
        status,
        updated_at: new Date().toISOString(),
        ...(status === 'contract_signed' && {
          signed_at: new Date().toISOString(),
        }),
      });

      if (error) {
        this.log('error', 'Failed to update contract setup status', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'Contract setup status updated', context);
      return { success: true };
    }, 'Contract setup status update failed');
  }

  /**
   * Get user's payment methods
   * Uses execute() for consistent error handling
   */
  async getPaymentMethods(
    userId: string,
  ): Promise<Result<PaymentMethodInfo[]>> {
    return this.execute(async () => {
      const context = { userId, operation: 'getPaymentMethods' };
      this.log('info', 'Fetching payment methods', context);

      const { data: paymentMethods, error } = await fetchAllRows((from, to) =>
        this.client
          .from('payment_methods')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .order('id')
          .range(from, to),
      );

      if (error) {
        this.log('error', 'Failed to get payment methods', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      const formattedMethods: PaymentMethodInfo[] = (paymentMethods || []).map(
        (method) => ({
          type: method.method_type as PaymentMethodInfo['type'],
          bankName: method.bank_name || undefined,
          accountLast4: method.account_last_4 || undefined,
          paypalEmail: method.paypal_email || undefined,
          cryptoAddress: method.crypto_address || undefined,
          isVerified: method.is_verified || false,
          createdAt: new Date(method.created_at),
        }),
      );

      this.log('info', 'Payment methods fetched successfully', {
        ...context,
        count: formattedMethods.length,
      });

      return formattedMethods;
    }, 'Failed to get payment methods');
  }

  // Private helper methods

  private async getProfileCompletionPercentage(
    userId: string,
  ): Promise<Result<number>> {
    return this.execute(async () => {
      const context = { userId, operation: 'getProfileCompletionPercentage' };

      // Single query with JOIN for atomic read and better performance
      const { data: profileData, error } = await this.client
        .from('profiles')
        .select(
          `
          phone_number,
          city,
          country_of_residence,
          professional_profiles!inner (
            bio,
            dance_styles,
            years_of_experience
          )
        `,
        )
        .eq('id', userId)
        .eq('professional_profiles.profile_type', 'dancer')
        .single();

      // Handle case where profile doesn't exist or has errors gracefully
      if (error && error.code !== 'PGRST116') {
        this.log('warn', 'Profile query failed', { ...context, error });
        return 0; // Return 0% completion if profile can't be loaded
      }

      // Gracefully handle missing profile data
      if (!profileData) {
        return 0;
      }

      // Access nested professional profile data
      const professionalProfile = Array.isArray(
        profileData.professional_profiles,
      )
        ? profileData.professional_profiles[0]
        : profileData.professional_profiles;

      // Simple completion check - gracefully handle missing data
      let completedFields = 0;
      const totalFields = 6; // Simplified for this example

      if (profileData.phone_number) completedFields++;
      if (profileData.city) completedFields++;
      if (profileData.country_of_residence) completedFields++;
      if (professionalProfile?.bio) completedFields++;
      if (
        professionalProfile?.dance_styles &&
        professionalProfile.dance_styles.length > 0
      )
        completedFields++;
      if (professionalProfile?.years_of_experience !== null) completedFields++;

      const percentage = Math.round((completedFields / totalFields) * 100);

      this.log('info', 'Profile completion calculated', {
        ...context,
        completedFields,
        totalFields,
        percentage,
        hasProfile: !!profileData,
        hasProfessionalProfile: !!professionalProfile,
      });

      return percentage;
    }, 'Profile completion calculation failed, defaulting to 0%');
  }

  private async hasPaymentMethod(userId: string): Promise<Result<boolean>> {
    return this.execute(async () => {
      const context = { userId, operation: 'hasPaymentMethod' };

      const { data: paymentMethod, error } = await this.client
        .from('payment_methods')
        .select('id')
        .eq('user_id', userId)
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        this.log('error', 'Failed to check payment method', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      return !!paymentMethod;
    }, 'Failed to check payment method');
  }

  private async getContractStatus(
    userId: string,
  ): Promise<Result<Date | null>> {
    return this.execute(async () => {
      const context = { userId, operation: 'getContractStatus' };

      const { data: contractSetup, error } = await this.client
        .from('contract_setups')
        .select('signed_at')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        this.log('error', 'Failed to get contract status', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      return contractSetup?.signed_at
        ? new Date(contractSetup.signed_at)
        : null;
    }, 'Failed to get contract status');
  }

  private determineContractStatus(
    profileCompletion: number,
    hasPaymentMethod: boolean,
    contractSignedAt: Date | null,
  ): ContractSetupStatus {
    // Check contract expiration (1 year from signing)
    if (contractSignedAt) {
      const oneYearFromSigning = new Date(contractSignedAt);
      oneYearFromSigning.setFullYear(oneYearFromSigning.getFullYear() + 1);
      if (new Date() > oneYearFromSigning) {
        return 'contract_expired';
      }
      return 'contract_signed';
    }

    // If payment is provided, contract is ready
    if (hasPaymentMethod) {
      return 'contract_review';
    }

    // If profile started, need payment
    if (profileCompletion > 0) {
      return 'payment_setup';
    }

    return 'not_started';
  }

  private buildContractSetupState(
    status: ContractSetupStatus,
    profileCompletion: number,
    hasPaymentMethod: boolean,
    contractSignedAt: Date | null,
  ): ContractSetupState {
    const baseState = {
      status,
      profileCompletion,
      hasPaymentMethod,
      contractSignedAt,
      canProceed: true,
    };

    switch (status) {
      case 'not_started':
        return {
          ...baseState,
          nextStepTitle: 'Complete Your Profile',
          nextStepDescription:
            'Finish setting up your dancer profile to start the contract process.',
          actionUrl: '/home/artist/profile/setup',
          actionLabel: 'Complete Profile',
          canProceed: true,
        };

      case 'profile_incomplete':
        return {
          ...baseState,
          nextStepTitle: 'Complete Your Profile',
          nextStepDescription: `Your profile is ${profileCompletion}% complete. Finish it to proceed with contract setup.`,
          actionUrl: '/home/artist/profile/setup',
          actionLabel: 'Complete Profile',
          canProceed: true,
        };

      case 'payment_setup':
        return {
          ...baseState,
          nextStepTitle: 'Add Payment Information',
          nextStepDescription:
            'Set up your payment method to receive payments for your work.',
          actionUrl: '/home/settings',
          actionLabel: 'Add Payment Method',
          canProceed: true,
        };

      case 'contract_review':
        return {
          ...baseState,
          nextStepTitle: 'Review & Sign Contract',
          nextStepDescription:
            'Review your contract terms and sign to complete the setup process.',
          actionUrl: '/home/settings',
          actionLabel: 'Review Contract',
          canProceed: true,
        };

      case 'contract_signed':
        return {
          ...baseState,
          nextStepTitle: 'Contract Active',
          nextStepDescription:
            'Your contract is active. You can now apply for opportunities and receive payments.',
          actionUrl: '/home/opportunities',
          actionLabel: 'Browse Opportunities',
          canProceed: false,
        };

      case 'contract_expired':
        return {
          ...baseState,
          nextStepTitle: 'Renew Contract',
          nextStepDescription:
            'Your contract has expired. Renew it to continue working on the platform.',
          actionUrl: '/home/settings',
          actionLabel: 'Renew Contract',
          canProceed: true,
        };

      default:
        return {
          ...baseState,
          nextStepTitle: 'Unknown Status',
          nextStepDescription: 'Please contact support for assistance.',
          actionUrl: '/support',
          actionLabel: 'Get Help',
          canProceed: false,
        };
    }
  }
}
